import { useMemo, useState } from "react";
import { Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, ReferenceLine } from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Info } from "lucide-react";
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface ABCItem {
  code: string;
  description: string;
  annualUsageValue: number;
  percentOfTotal: number;
  cumulativePercent: number;
  currentClassification: "A" | "B" | "C";
  proposedClassification: "A" | "B" | "C";
}

interface ParetoChartProps {
  items: ABCItem[];
  aThreshold: number;
  bThreshold: number;
}

type VisualizationMode = "top-items" | "aggregated" | "sampled";

export function ParetoChart({ items, aThreshold, bThreshold }: ParetoChartProps) {
  const [visualizationMode, setVisualizationMode] = useState<VisualizationMode>("aggregated");
  const [topItemsCount, setTopItemsCount] = useState<number>(100);

  const totalItems = items.length;

  // Determine best default mode based on item count
  useMemo(() => {
    if (totalItems <= 200) {
      setVisualizationMode("top-items");
      setTopItemsCount(totalItems);
    } else if (totalItems <= 1000) {
      setVisualizationMode("top-items");
      setTopItemsCount(200);
    } else {
      setVisualizationMode("aggregated");
    }
  }, [totalItems]);

  const chartData = useMemo(() => {
    if (visualizationMode === "top-items") {
      // Show top N items individually
      const topItems = items.slice(0, topItemsCount);
      return topItems.map((item, index) => ({
        name: item.code,
        displayName: `#${index + 1}`,
        value: item.annualUsageValue,
        cumulative: item.cumulativePercent,
        classification: item.proposedClassification,
        index: index + 1,
        itemCount: 1,
      }));
    } else if (visualizationMode === "aggregated") {
      // Aggregate items by classification with smart bucketing
      const result: any[] = [];
      let currentBucket: ABCItem[] = [];
      let bucketStartIndex = 0;
      const bucketSize = Math.max(10, Math.floor(totalItems / 100)); // Create ~100 buckets

      items.forEach((item, index) => {
        currentBucket.push(item);

        // Create bucket when:
        // 1. Bucket is full, OR
        // 2. Classification changes, OR
        // 3. Last item
        const nextItem = items[index + 1];
        const classificationChanges = nextItem && nextItem.proposedClassification !== item.proposedClassification;
        const bucketFull = currentBucket.length >= bucketSize;
        const isLastItem = index === items.length - 1;

        if (bucketFull || classificationChanges || isLastItem) {
          const bucketValue = currentBucket.reduce((sum, i) => sum + i.annualUsageValue, 0);
          const avgCumulative = currentBucket.reduce((sum, i) => sum + i.cumulativePercent, 0) / currentBucket.length;
          const classification = currentBucket[0].proposedClassification;

          result.push({
            name: `Items ${bucketStartIndex + 1}-${index + 1}`,
            displayName: `${bucketStartIndex + 1}-${index + 1}`,
            value: bucketValue,
            cumulative: avgCumulative,
            classification: classification,
            index: result.length + 1,
            itemCount: currentBucket.length,
          });

          currentBucket = [];
          bucketStartIndex = index + 1;
        }
      });

      return result;
    } else {
      // Sampled mode: Take strategic samples across the distribution
      const result: any[] = [];
      const sampleSize = 100;
      
      // Always include top items (they're most important)
      const topCount = Math.min(50, Math.floor(totalItems * 0.05));
      for (let i = 0; i < topCount; i++) {
        const item = items[i];
        result.push({
          name: item.code,
          displayName: `#${i + 1}`,
          value: item.annualUsageValue,
          cumulative: item.cumulativePercent,
          classification: item.proposedClassification,
          index: i + 1,
          itemCount: 1,
        });
      }

      // Sample remaining items
      const remainingCount = sampleSize - topCount;
      const step = Math.floor((totalItems - topCount) / remainingCount);
      
      for (let i = 0; i < remainingCount; i++) {
        const index = topCount + (i * step);
        if (index < totalItems) {
          const item = items[index];
          result.push({
            name: item.code,
            displayName: `#${index + 1}`,
            value: item.annualUsageValue,
            cumulative: item.cumulativePercent,
            classification: item.proposedClassification,
            index: index + 1,
            itemCount: 1,
          });
        }
      }

      return result;
    }
  }, [items, visualizationMode, topItemsCount, totalItems]);

  const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: any[] }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-background border rounded-lg shadow-lg p-3">
          <p className="font-semibold">{data.name}</p>
          {data.itemCount > 1 && (
            <p className="text-xs text-muted-foreground">
              ({data.itemCount} items aggregated)
            </p>
          )}
          <p className="text-sm text-muted-foreground">
            Value: ${data.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </p>
          <p className="text-sm text-muted-foreground">
            Cumulative: {data.cumulative.toFixed(2)}%
          </p>
          <p className="text-sm">
            <span className={`inline-block w-2 h-2 rounded-full mr-1 ${
              data.classification === "A" ? "bg-red-600" :
              data.classification === "B" ? "bg-yellow-600" :
              "bg-green-600"
            }`} />
            Class {data.classification}
          </p>
        </div>
      );
    }
    return null;
  };

  const getModeDescription = () => {
    switch (visualizationMode) {
      case "top-items":
        return `Showing top ${topItemsCount} items individually. Best for detailed analysis of high-value items.`;
      case "aggregated":
        return `Items grouped into ~${chartData.length} buckets by classification. Best for large datasets (${totalItems.toLocaleString()} items).`;
      case "sampled":
        return `Strategic sampling of ${chartData.length} items across the distribution. Best for quick overview.`;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle>Pareto Analysis Chart</CardTitle>
            <CardDescription>
              {getModeDescription()}
            </CardDescription>
          </div>
          <TooltipProvider>
            <UITooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-sm">
                <p className="text-sm">
                  The Pareto chart shows the 80/20 rule: typically 20% of items represent 80% of value.
                  The bars show item values, and the line shows cumulative percentage.
                </p>
              </TooltipContent>
            </UITooltip>
          </TooltipProvider>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Visualization Controls */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
          <div className="space-y-2">
            <Label>Visualization Mode</Label>
            <RadioGroup value={visualizationMode} onValueChange={(v) => setVisualizationMode(v as VisualizationMode)}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="aggregated" id="aggregated" />
                <Label htmlFor="aggregated" className="font-normal cursor-pointer">
                  Aggregated (Recommended for {">"}1,000 items)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="top-items" id="top-items" />
                <Label htmlFor="top-items" className="font-normal cursor-pointer">
                  Top Items (Individual bars)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="sampled" id="sampled" />
                <Label htmlFor="sampled" className="font-normal cursor-pointer">
                  Sampled (Quick overview)
                </Label>
              </div>
            </RadioGroup>
          </div>

          {visualizationMode === "top-items" && (
            <div className="space-y-2">
              <Label>Number of Items to Display</Label>
              <Select value={topItemsCount.toString()} onValueChange={(v) => setTopItemsCount(Number(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="50">Top 50 items</SelectItem>
                  <SelectItem value="100">Top 100 items</SelectItem>
                  <SelectItem value="200">Top 200 items</SelectItem>
                  <SelectItem value="500">Top 500 items</SelectItem>
                  {totalItems > 500 && <SelectItem value="1000">Top 1,000 items</SelectItem>}
                  {totalItems > 1000 && <SelectItem value={totalItems.toString()}>All {totalItems.toLocaleString()} items</SelectItem>}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {topItemsCount >= 500 && "⚠️ Large datasets may impact performance"}
              </p>
            </div>
          )}
        </div>

        {/* Chart */}
        <div className="w-full h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis 
                dataKey="displayName" 
                label={{ value: "Items (sorted by value)", position: "insideBottom", offset: -10 }}
                tick={{ fontSize: 10 }}
                interval={Math.floor(chartData.length / 10)}
              />
              <YAxis 
                yAxisId="left"
                label={{ value: "Annual Usage Value ($)", angle: -90, position: "insideLeft" }}
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => {
                  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                  if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
                  return `${value}`;
                }}
              />
              <YAxis 
                yAxisId="right" 
                orientation="right"
                domain={[0, 100]}
                label={{ value: "Cumulative %", angle: 90, position: "insideRight" }}
                tick={{ fontSize: 12 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ paddingTop: "20px" }}
                iconType="circle"
              />
              
              {/* Reference lines for thresholds */}
              <ReferenceLine 
                yAxisId="right"
                y={aThreshold} 
                stroke="rgb(220, 38, 38)" 
                strokeDasharray="3 3"
                label={{ value: `A-Class (${aThreshold}%)`, position: "right", fill: "rgb(220, 38, 38)", fontSize: 12 }}
              />
              <ReferenceLine 
                yAxisId="right"
                y={aThreshold + bThreshold} 
                stroke="rgb(202, 138, 4)" 
                strokeDasharray="3 3"
                label={{ value: `B-Class (${aThreshold + bThreshold}%)`, position: "right", fill: "rgb(202, 138, 4)", fontSize: 12 }}
              />
              
              {/* Bars colored by classification */}
              <Bar 
                yAxisId="left"
                dataKey="value" 
                name="Annual Usage Value"
                fill="#8884d8"
                shape={(props: any) => {
                  const { x, y, width, height, classification } = props;
                  const fill = 
                    classification === "A" ? "rgb(220, 38, 38)" :
                    classification === "B" ? "rgb(202, 138, 4)" :
                    "rgb(22, 163, 74)";
                  return <rect x={x} y={y} width={width} height={height} fill={fill} />;
                }}
              />
              
              {/* Cumulative percentage line */}
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="cumulative" 
                name="Cumulative %"
                stroke="rgb(59, 130, 246)" 
                strokeWidth={2}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4 pt-4 border-t">
          <div className="text-center">
            <p className="text-2xl font-bold text-red-600">
              {items.filter(i => i.proposedClassification === "A").length}
            </p>
            <p className="text-sm text-muted-foreground">A-Class Items</p>
            <p className="text-xs text-muted-foreground">
              ({((items.filter(i => i.proposedClassification === "A").length / totalItems) * 100).toFixed(1)}%)
            </p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-yellow-600">
              {items.filter(i => i.proposedClassification === "B").length}
            </p>
            <p className="text-sm text-muted-foreground">B-Class Items</p>
            <p className="text-xs text-muted-foreground">
              ({((items.filter(i => i.proposedClassification === "B").length / totalItems) * 100).toFixed(1)}%)
            </p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600">
              {items.filter(i => i.proposedClassification === "C").length}
            </p>
            <p className="text-sm text-muted-foreground">C-Class Items</p>
            <p className="text-xs text-muted-foreground">
              ({((items.filter(i => i.proposedClassification === "C").length / totalItems) * 100).toFixed(1)}%)
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
